import { useLocalSearchParams } from 'expo-router'
import { StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { DEFAULT_PERIOD_TYPE, DEFAULT_WEEK_START_DAY } from '@/constants/periods'
import { useAnalyticsData } from '@/hooks/useAnalyticsData'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useOrgSettings } from '@/hooks/useOrgSettings'
import { usePeriodNavigation } from '@/hooks/usePeriodNavigation'
import { useAuth } from '@/lib/auth'
import type { PeriodConfig, WeekStartDay } from '@/types/periods'
import { HoursBarChart } from '@/ui/analytics/HoursBarChart'
import { StatCardRow } from '@/ui/analytics/StatCardRow'

import { Card } from '@/ui/Card'
import { EmptyCard } from '@/ui/EmptyCard'
import { LoadingIndicator } from '@/ui/LoadingIndicator'
import { Page } from '@/ui/Page'
import { PeriodNavigator } from '@/ui/PeriodNavigator'

export default function AnalyticsPage() {
    useAuth()
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const theme = useTheme()
    const { role } = useCurrentOrg()

    const { settings, isLoading: settingsLoading } = useOrgSettings(params.orgSlug)
    const periodConfig: PeriodConfig = {
        periodType: settings?.period_type || DEFAULT_PERIOD_TYPE,
        weekStartDay: (settings?.week_start_day ?? DEFAULT_WEEK_START_DAY) as WeekStartDay,
    }

    const { periodDate, handlePrevious, handleNext, handleDatePicked } =
        usePeriodNavigation(periodConfig)

    const analytics = useAnalyticsData(periodDate, periodConfig, !settingsLoading)
    const isAdmin = role === 'admin' || role === 'clerical'

    if (!isAdmin) {
        return (
            <Page>
                <View style={styles.restricted}>
                    <Text variant="bodyLarge" style={{ color: theme.colors.onSurfaceVariant }}>
                        Analytics is only available for admin and clerical users.
                    </Text>
                </View>
            </Page>
        )
    }

    if (settingsLoading || analytics.isLoading) {
        return <LoadingIndicator />
    }

    return (
        <Page>
            <PeriodNavigator
                periodLabel={analytics.periodLabel}
                onPrevious={handlePrevious}
                onNext={handleNext}
                periodDate={periodDate}
                onDatePicked={handleDatePicked}
            />

            {!analytics.hasData ? (
                <EmptyCard
                    icon="chart-gantt"
                    title="No data"
                    subtitle="No time entries found for this period"
                />
            ) : (
                <>
                    <StatCardRow
                        totalHours={analytics.totalHours}
                        totalEntries={analytics.totalEntries}
                        activeUsers={analytics.activeUsers}
                        avgHoursPerUser={analytics.avgHoursPerUser}
                        hoursChangePercent={analytics.hoursChangePercent}
                    />

                    <Card>
                        <Card.Title title="Hours by Team Member" />
                        <Card.Content>
                            <HoursBarChart items={analytics.hoursByUser} />
                        </Card.Content>
                    </Card>

                    <Card>
                        <Card.Title title="Top Jobs" />
                        <Card.Content>
                            <HoursBarChart items={analytics.hoursByJob} />
                        </Card.Content>
                    </Card>

                    <Card>
                        <Card.Title title="Hours by Customer" />
                        <Card.Content>
                            <HoursBarChart items={analytics.hoursByCustomer} />
                        </Card.Content>
                    </Card>
                </>
            )}
        </Page>
    )
}

const styles = StyleSheet.create({
    restricted: {
        padding: 32,
        alignItems: 'center',
    },
})
